import React, { useState, useMemo } from "react";
import { TextField, InputAdornment, Popover, Box } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import { format } from "date-fns";
import DateRangePickerContent from "./internal/DateRangePickerContent";
import {
  ShortcutKey,
  SHORTCUT_CONFIGS,
} from "./internal/DateRangePickerShortcuts";
import { useTranslation } from "react-i18next";
import useStyles from "./DateRangeField.styles";
import { getLocalizationAdapterLocale } from "@/utils/utils";
import { MelaButton, MELA_PHONE_MEDIA } from "@/ui";

const DATE_RANGE_POPOVER_Z_INDEX = 30040;
export type DateRangeFieldDisplayFormat = "default" | "short";

export interface DateRangeFieldProps {
  id?: string;
  value: [Date | null, Date | null];
  onChange: (range: [Date | null, Date | null]) => void;
  label?: string | boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  maxRangeDays?: number;
  shortcuts?: ShortcutKey[];
  defaultRange?: ShortcutKey;
  clearable?: boolean;
  fullWidth?: boolean;
  sx?: any;
  className?: string;
  displayFormat?: DateRangeFieldDisplayFormat;
}

const formatDisplayDate = (
  date: Date,
  language: string,
  displayFormat: DateRangeFieldDisplayFormat,
): string => {
  if (displayFormat === "short") {
    return new Intl.DateTimeFormat(language, {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).format(date);
  }

  return format(date, "dd MMM yyyy", {
    locale: getLocalizationAdapterLocale(language),
  });
};

const DateRangeField = ({
  id: inputId,
  value,
  onChange,
  label = true,
  placeholder,
  minDate,
  maxDate,
  maxRangeDays,
  shortcuts,
  className,
  defaultRange,
  clearable = false,
  fullWidth = false,
  sx,
  displayFormat = "default",
}: DateRangeFieldProps) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (defaultRange && !value[0] && !value[1]) {
      const config = SHORTCUT_CONFIGS[defaultRange];
      if (config) {
        const [start, end] = config.getRange();
        onChange([start, end]);
      }
    }
  }, [defaultRange, value, onChange]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApply = (newRange: [Date | null, Date | null]) => {
    onChange(newRange);
    handleClose();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([null, null]);
  };

  const displayValue = useMemo(() => {
    const [start, end] = value;
    if (!start && !end) return "";
    const startStr = start
      ? formatDisplayDate(start, i18n.language, displayFormat)
      : "...";
    const endStr = end
      ? formatDisplayDate(end, i18n.language, displayFormat)
      : "...";
    return `${startStr} - ${endStr}`;
  }, [displayFormat, value, i18n.language]);

  const open = Boolean(anchorEl);
  const id = open ? "date-range-popover" : undefined;

  const finalLabel = useMemo(() => {
    if (label === false) return undefined;
    if (label === true) return t("ui.dataRange.label");
    return label;
  }, [label, t]);

  return (
    <Box
      className={className}
      sx={{
        position: "relative",
        display: fullWidth ? "flex" : "inline-flex",
        [MELA_PHONE_MEDIA]: { maxWidth: "100%" },
        ...sx,
      }}
    >
      <TextField
        id={inputId}
        fullWidth={fullWidth}
        label={finalLabel}
        placeholder={placeholder}
        value={displayValue}
        onClick={handleClick}
        autoComplete="off"
        size="small"
        slotProps={{
          inputLabel: { shrink: true },
          htmlInput: { readOnly: true, style: { cursor: "pointer" } },
          input: {
            className: styles.textField,
            endAdornment: (
              <InputAdornment position="end">
                {clearable && (value[0] || value[1]) && (
                  <MelaButton
                    iconOnly
                    size="sm"
                    variant="ghost"
                    tone="neutral"
                    onClick={handleClear}
                    aria-label={t("common.clear.translation", "Clear")}
                    startIcon={<ClearIcon fontSize="small" />}
                    style={{
                      minWidth: 24,
                      width: 24,
                      height: 24,
                      padding: 0,
                      borderRadius: "50%",
                      boxShadow: "none",
                      marginRight: 4,
                    }}
                  />
                )}
                <CalendarTodayIcon
                  fontSize="small"
                  className={styles.calendarAdornment}
                />
              </InputAdornment>
            ),
          },
        }}
        sx={{
          width: fullWidth ? "100%" : "350px",
        }}
      />
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{ zIndex: DATE_RANGE_POPOVER_Z_INDEX }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            className: styles.popoverPaper,
          },
        }}
      >
        <DateRangePickerContent
          initialRange={value}
          onApply={handleApply}
          onCancel={handleClose}
          minDate={minDate}
          maxDate={maxDate}
          maxRangeDays={maxRangeDays}
          shortcuts={shortcuts}
        />
      </Popover>
    </Box>
  );
};

export default DateRangeField;
